// src/components/CatalogList.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import './CatalogList.css';
import { useCartStore } from '../../store/cartStore';
import { useUserStore } from '../../store/userStore';
import { AgGridReact } from 'ag-grid-react';
import { formatCurrency } from '../../utils/currency';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

type CatalogItem = {
  itemNumber: number;
  partNumber: string | null;
  description: string | null;
  qty: number;
  uom?: string | null;
  price?: number | null;
  partId?: number | null;
  figId?: number | null;
};

type Props = {
  bomJson: CatalogItem[];
  preselectedItemNumber?: number;
  layoutVariant?: 'default' | 'componentDetail';
};

const CatalogList: React.FC<Props> = ({ bomJson, preselectedItemNumber, layoutVariant = 'default' }) => {
  const role = String(useUserStore((s) => s.user?.role) ?? '').trim().toUpperCase();
  const isVendor = role.includes('VENDOR');
  const isComponentDetail = layoutVariant === 'componentDetail';

  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const { cart, refreshOpenCart, addItem, updateQty, loadSummary } = useCartStore();

  const [open, setOpen] = useState(false);
  const [row, setRow] = useState<CatalogItem | null>(null);
  const [qty, setQty] = useState<number>(1);
  const [existingQty, setExistingQty] = useState<number>(0);
  const [cartItemId, setCartItemId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const lastPreselectedRef = useRef<string>('');

  // === Checkbox / SVG Sync ===
  const handleCheckboxChange = (itemNumber: number) => {
    const key = String(itemNumber);
    const isSelected = selectedItems.includes(key);
    const next = isSelected ? selectedItems.filter((x) => x !== key) : [...selectedItems, key];
    setSelectedItems(next);
    window.dispatchEvent(new CustomEvent('bomToggle', { detail: { itemNumber: key, selected: !isSelected } }));
  };

  useEffect(() => {
    const onSvgToggle = (e: Event) => {
      const { itemNumber, selected } = (e as CustomEvent).detail;
      setSelectedItems((prev) => {
        const has = prev.includes(itemNumber);
        if (selected && !has) return [...prev, itemNumber];
        if (!selected && has) return prev.filter((x) => x !== itemNumber);
        return prev;
      });
    };
    window.addEventListener('svgTextToggle', onSvgToggle);
    return () => window.removeEventListener('svgTextToggle', onSvgToggle);
  }, []);

  useEffect(() => {
    if (!Array.isArray(bomJson) || bomJson.length === 0) {
      setSelectedItems([]);
      lastPreselectedRef.current = '';
      return;
    }

    const key =
      Number.isFinite(preselectedItemNumber as number) && Number(preselectedItemNumber) > 0
        ? String(Number(preselectedItemNumber))
        : '';

    if (!key) {
      setSelectedItems([]);
      lastPreselectedRef.current = '';
      return;
    }

    const exists = bomJson.some((item) => String(item.itemNumber) === key);
    if (!exists) {
      setSelectedItems([]);
      lastPreselectedRef.current = '';
      return;
    }

    setSelectedItems([key]);
    if (lastPreselectedRef.current !== key) {
      window.dispatchEvent(new CustomEvent('bomToggle', { detail: { itemNumber: key, selected: true } }));
      lastPreselectedRef.current = key;
    }
  }, [bomJson, preselectedItemNumber]);

  // === Add-to-cart modal logic ===
  const openAddModal = async (r: CatalogItem) => {
    if (!isVendor) return;
    await refreshOpenCart();
    let existQty = 0;
    let existCartItemId: number | null = null;

    if (cart?.items && r.partId != null) {
      const line = cart.items.find((li) => li.partId === r.partId);
      if (line) {
        existQty = line.quantity;
        existCartItemId = line.cartItemId;
      }
    }

    setRow(r);
    setExistingQty(existQty);
    setCartItemId(existCartItemId);
    setQty(Math.max(1, existQty || r.qty || 1));
    setOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setOpen(false);
    setRow(null);
    setQty(1);
    setExistingQty(0);
    setCartItemId(null);
  };

  const dec = () => setQty((q) => Math.max(1, q - 1));
  const inc = () => setQty((q) => q + 1);

  const onConfirm = async () => {
    if (!row?.partId) {
      alert('Missing partId for this part; cannot add to cart.');
      return;
    }
    try {
      setSaving(true);
      if (cartItemId) {
        await updateQty(cartItemId, qty);
      } else {
        await addItem({
          partId: row.partId,
          figId: row.figId ?? null,
          itemNo: String(row.itemNumber),
          quantity: qty,
        });
      }
      await loadSummary();
      closeModal();
    } catch (e: any) {
      console.error('Cart add/update failed:', e?.message || e);
      alert(e?.message || 'Failed to update cart');
    } finally {
      setSaving(false);
    }
  };

  // === Modal ===
  const modal = useMemo(() => {
    if (!isVendor || !open || !row) return null;
    const unitPrice = row.price ?? 0;
    const lineTotal = unitPrice * qty;
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
        }}
        onClick={closeModal}
      >
        <div
          style={{
            width: 540,
            maxWidth: '92vw',
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
            padding: 20,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 18 }}>
              {row.partNumber} • {row.description ?? ''}
            </div>
            <div style={{ color: '#555', fontSize: 13, marginTop: 2 }}>
              Item #{row.itemNumber} | UOM: {row.uom ?? '-'} | Price: {formatCurrency(unitPrice)}
            </div>
          </div>

          {existingQty > 0 ? (
            <div style={{ marginBottom: 8, fontSize: 13, color: '#0a7' }}>
              Already in cart: <b>{existingQty}</b>
            </div>
          ) : (
            <div style={{ marginBottom: 8, fontSize: 13, color: '#888' }}>Not yet in cart</div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <button onClick={dec} disabled={saving} style={{ padding: '6px 10px' }}>
              −
            </button>
            <input
              type="number"
              min={1}
              value={qty}
              onChange={(e) => setQty(Math.max(1, Number(e.target.value || 1)))}
              disabled={saving}
              style={{ width: 90, textAlign: 'center', padding: '6px 8px' }}
            />
            <button onClick={inc} disabled={saving} style={{ padding: '6px 10px' }}>
              +
            </button>

            <div style={{ marginLeft: 'auto', color: '#333' }}>
              Line total: <b>{formatCurrency(lineTotal)}</b>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button onClick={closeModal} disabled={saving} style={{ padding: '8px 14px' }}>
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={saving}
              style={{
                padding: '8px 14px',
                background: '#1f8ef1',
                color: '#fff',
                border: 0,
                borderRadius: 6,
              }}
            >
              {cartItemId ? 'Update quantity' : 'Add to cart'}
            </button>
          </div>
        </div>
      </div>
    );
  }, [isVendor, open, row, qty, existingQty, saving]);

  // === AG GRID ===
  const columnDefs = useMemo(() => {
    const cols: any[] = [
      {
        headerName: 'Select',
        field: 'itemNumber',
        width: 58,
        cellRenderer: (params: any) => {
          const isSelected = selectedItems.includes(String(params.data.itemNumber));
          return (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => handleCheckboxChange(params.data.itemNumber)}
            />
          );
        },
      },
      { headerName: 'Pos.', field: 'itemNumber', width: 65 },
      { headerName: 'Cod.', field: 'partNumber', flex: 1, minWidth: 100 },
      { headerName: 'Descrizione', field: 'description', flex: 2, wrapText: true, autoHeight: true , minWidth: 120},
      // {
      //   headerName: 'UOM',
      //   field: 'uom',
      //   width: isComponentDetail ? 85 : 50,
      //   headerClass: 'ag-header-center',
      //   cellClass: 'ag-cell-center',
      // },
      {
        headerName: 'Price',
        field: 'price',
        width: 90,
        headerClass: 'ag-header-center',
        cellClass: 'ag-cell-center',
        valueFormatter: (p: any) => (p.value != null ? formatCurrency(p.value) : '-'),
      },
      {
        headerName: isComponentDetail ? 'Q.tà' : 'BOM Qty',
        field: 'qty',
        width: isComponentDetail ? 60 : 75,
        headerClass: 'ag-header-center',
        cellClass: 'ag-cell-center',
      },
    ];

    if (isVendor) {
      cols.push({
        headerName: 'Action',
        field: 'action',
        width: 80,
        cellRenderer: (params: any) => (
          <button
  title="Add to cart"
  onClick={(e) => {
    e.stopPropagation();
    openAddModal(params.data);
  }}
  className="btn btn-sm btn-outline-primary"
  style={{
    transition: 'background-color 0.2s, color 0.2s',
  }}
  onMouseEnter={(e) => {
    (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#820fbbff'; // dark green
    (e.currentTarget as HTMLButtonElement).style.color = '#fff';
  }}
  onMouseLeave={(e) => {
    (e.currentTarget as HTMLButtonElement).style.backgroundColor = '';
    (e.currentTarget as HTMLButtonElement).style.color = '';
  }}
>
  🛒 Add
</button>

        ),
      });
    }
    return cols;
  }, [isVendor, selectedItems, isComponentDetail]);

  return (
    <div className="container my-2 border-0" style={{maxHeight:"100vh"}}>
      <div className="card shadow-sm border-0" >
        <div className="card-header text-white fw-semibold" style={{ backgroundColor: '#7e1af8ff' }}>
          Bill of Materials
        </div>
        <div className="card-body p-0" >
          <div
            className="ag-theme-alpine"
            style={{ height: '100vh', width: '100%', borderRadius: '8px', overflow: 'hidden' }}
          >
           <AgGridReact
  rowData={bomJson}
  columnDefs={columnDefs}
  domLayout="normal"      // allow scrolling inside container
  suppressRowClickSelection
  animateRows
 
  // remove pagination
/>

          </div>
        </div>
      </div>
      {modal}
    </div>
  );
};

export default CatalogList;
